import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'sidebar-nav-collapsed';
const MOBILE_BREAKPOINT = 768;

interface SidebarNavContextValue {
  isCollapsed: boolean;
  isMobile: boolean;
  toggleCollapsed: () => void;
}

const SidebarNavContext = createContext<SidebarNavContextValue>({
  isCollapsed: false,
  isMobile: false,
  toggleCollapsed: () => {},
});

export const SidebarNavProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Initialise synchronously so the first render already knows the viewport,
  // preventing a flash of the desktop sidebar on narrow screens.
  const [isMobile, setIsMobile] = useState<boolean>(
    () => window.innerWidth < MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    let prevMobile = window.innerWidth < MOBILE_BREAKPOINT;

    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (mobile === prevMobile) return;
      prevMobile = mobile;
      setIsMobile(mobile);
      // Entering mobile: hide the sidebar so content fills the screen.
      if (mobile) setIsCollapsed(true);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ isCollapsed, isMobile, toggleCollapsed }),
    [isCollapsed, isMobile, toggleCollapsed],
  );

  return (
    <SidebarNavContext.Provider value={value}>
      {children}
    </SidebarNavContext.Provider>
  );
};

export const useSidebarNav = () => useContext(SidebarNavContext);
